"use client";
import {
  Accordion,
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  useBetaDataTableContext as useTableContext,
} from "@zapier/design-system";

import { Connection, useBulkDeleteAuthentications } from "@zapier/client";
import { useNotification } from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";
import { FlexSpacer } from "@zapier/shared";
import {
  getServiceIconUrl,
  UNTITLED_CONNECTION_DEFAULT,
  UNTITLED_CONNECTION_TITLE,
} from "@zapier/utils";
import { useMemo } from "react";

import styles from "./ConnectionsBulkDeleteModal.module.css";

interface Props {
  connections: Connection[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

function getConnectionIcon(connection: Connection): string {
  const slug = connection.selectedApi?.split("@")[0] ?? "";
  return connection.implementation?.images?.url64X64 ?? getServiceIconUrl(slug);
}

function getConnectionDisplayName(connection: Connection): string {
  if (connection.title === UNTITLED_CONNECTION_TITLE) {
    return UNTITLED_CONNECTION_DEFAULT;
  }
  return connection.title ?? UNTITLED_CONNECTION_DEFAULT;
}

function ConnectionListItem({ connection }: { connection: Connection }) {
  return (
    <div className={styles.connectionItem}>
      {/* eslint-disable-next-line next/no-img-element -- dynamic service icon URL from the app registry, not a Next.js-optimizable local asset */}
      <img
        src={getConnectionIcon(connection)}
        alt=""
        className={styles.connectionIcon}
      />
      <Text type="Body2">{getConnectionDisplayName(connection)}</Text>
    </div>
  );
}

export function ConnectionsBulkDeleteModal({
  connections,
  isOpen,
  setIsOpen,
}: Props) {
  const t = useTranslations("connections-bulk-delete-modal");
  const { createNotification } = useNotification();
  const { bulkDeleteAuthentications, isLoading } =
    useBulkDeleteAuthentications();
  const { clearSelection } = useTableContext();

  const { deletable, undeletable } = useMemo(() => {
    const deletable: Connection[] = [];
    const undeletable: Connection[] = [];
    for (const c of connections) {
      if (c.permissions?.delete) {
        deletable.push(c);
      } else {
        undeletable.push(c);
      }
    }
    return { deletable, undeletable };
  }, [connections]);

  const deletableCount = deletable.length;

  const submit = async () => {
    if (deletableCount === 0) return;

    const ids = deletable.map((c) => c.id);
    setIsOpen(false);

    try {
      const result = await bulkDeleteAuthentications({ ids });

      if (result.failed === 0) {
        createNotification({
          message: (
            <Text>
              {t("notifications.success-message", {
                count: result.successful,
              })}
            </Text>
          ),
          type: "success",
        });
      } else {
        createNotification({
          message: (
            <Text>
              {t("notifications.partial-failure-message", {
                successful: result.successful,
                failed: result.failed,
              })}
            </Text>
          ),
          type: "error",
        });
      }
    } catch {
      createNotification({
        message: <Text>{t("notifications.error-message")}</Text>,
        type: "error",
      });
    }

    clearSelection();
  };

  if (!isOpen) return;

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- the form deliberately stops click propagation to keep the modal open and submits via its submit button */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <ModalDialogHeader textAlign="start">
          {t("header.title", { count: deletableCount })}
        </ModalDialogHeader>

        <ModalDialogBody
          textAlign="start"
          style={{ overflowY: "auto", maxHeight: "50vh" }}
        >
          <div className={styles.accordionContainer}>
            <Accordion
              title={t("body.deletable-title", { count: deletableCount })}
              isInitiallyOpen={true}
              toggleSide="left"
            >
              <div className={styles.connectionList}>
                {deletable.map((c) => (
                  <ConnectionListItem key={c.id} connection={c} />
                ))}
              </div>
            </Accordion>

            {undeletable.length > 0 && (
              <Accordion
                title={t("body.undeletable-title", {
                  count: undeletable.length,
                })}
                isInitiallyOpen={true}
                toggleSide="left"
              >
                <div className={styles.connectionList}>
                  {undeletable.map((c) => (
                    <ConnectionListItem key={c.id} connection={c} />
                  ))}
                </div>
              </Accordion>
            )}
          </div>
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            variant="tertiary"
            onPress={() => setIsOpen(false)}
            type="button"
          >
            {t("buttons.cancel")}
          </Button>
          <FlexSpacer />
          <Button
            type="submit"
            variant="danger"
            isDisabled={isLoading || deletableCount === 0}
          >
            {t("buttons.delete", { count: deletableCount })}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
